import type { MapDocument } from '../map/types.ts';
import type { RaceState } from '../race.ts';
import type { HullDimensions } from '../hullSize.ts';
import type { PlacedHull } from '../placement.ts';
import type { FleetContext } from '../turn.ts';
import type { TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { prepareCourse } from './course.ts';
import { decideMove } from './expert.ts';
import { BOT_LEVEL_KNOBS, type BotLevelKnobs } from './levels.ts';
import { botTurnRng } from './runRace.ts';
import type { LiveBotLevel } from './seats.ts';
import { chooseStart, type BotStart } from './start.ts';

export type BotStartRequest = {
  kind: 'start';
  /** Boats already planted this race; the bot keeps the placement gap to each. */
  placed: readonly PlacedHull[];
  /** This seat's hull. */
  hull: HullDimensions;
  /** This seat's feel, so the route out of the start fits the hull. */
  feel?: TurnFeel;
};

/** Another seat, as a bot sees it: enough to predict that boat's own line. */
export type RivalSeat = {
  id: number;
  boat: BoatState;
  race: RaceState;
  feel: TurnFeel;
  dimensions: HullDimensions;
  finished: boolean;
};

export type BotMoveRequest = {
  kind: 'move';
  boat: BoatState;
  race: RaceState;
  /** The feel the scene will commit with, so the bot plans the move it will actually get. */
  feel: TurnFeel;
  /**
   * Parked hulls for a sequential click. Omitted on a simultaneous lock-in, which stores the
   * aimed path and lets `resolveRound` sweep once every path exists.
   */
  fleet?: FleetContext;
  /** Every other seat with its race and feel. Sequential only; omitted on a lock-in. */
  rivals?: readonly RivalSeat[];
  level: LiveBotLevel;
  /** The knobs this seat races with (a saved profile's, or its level's). Omitted: the level's. */
  knobs?: BotLevelKnobs;
  seed: number;
  seat: number;
  /** 1-based turn this seat is about to sail. */
  turn: number;
};

export type BotRequest = BotStartRequest | BotMoveRequest;

export type BotReply =
  | { kind: 'start'; start: BotStart | null }
  | { kind: 'move'; destination: Vec2 };

/** Main thread to worker. `init` is sent once per scene; each request carries an id for its reply. */
export type BotWorkerInbound =
  | { type: 'init'; map: MapDocument; mapId: string; boatCollisions: boolean }
  | { type: 'request'; id: number; request: BotRequest };

export type BotWorkerOutbound =
  | { type: 'reply'; id: number; reply: BotReply }
  | { type: 'error'; id: number; message: string };

export type BotPilot = {
  answer: (request: BotRequest) => BotReply;
};

/**
 * One map's bot brain. The course (raster, then each hull's gate fields) is built from the map
 * it is handed, on first use, and every seat shares it. Pure apart from each seat's warm line,
 * so the same code runs in the Vite worker and as the main-thread fallback.
 */
export function createBotPilot(map: MapDocument, mapId: string): BotPilot {
  const course = prepareCourse(map);
  /** Each seat's planned clicks after the one it last sailed, keyed to the turn they follow. */
  const plans = new Map<number, { turn: number; plan: Vec2[] }>();

  return {
    answer(request) {
      if (request.kind === 'start') {
        return {
          kind: 'start',
          start: chooseStart(course, { placed: request.placed, hull: request.hull, feel: request.feel }),
        };
      }

      const previous = plans.get(request.seat);
      const decision = decideMove(course, request.feel, request.boat, request.race, {
        knobs: request.knobs ?? BOT_LEVEL_KNOBS[request.level],
        fleet: request.fleet,
        rivals: request.rivals,
        warm: previous && previous.turn === request.turn - 1 ? previous.plan : undefined,
        rng: botTurnRng(request.seed, mapId, request.seat, request.turn),
      });
      plans.set(request.seat, { turn: request.turn, plan: decision.plan });

      return { kind: 'move', destination: decision.destination };
    },
  };
}
